import React, { useEffect, useState, useContext } from "react";
import { Redirect, withRouter, Link } from "react-router-dom";
import axios from "axios";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";

import { subirArchivo } from "../../utils/utilidadesArchivos";
import AuthContext from "../../context/AuthContext";
import useObtenerUsuario from "../../utils/useObtenerUsuario";
import useObtenerMunProp from "../../utils/useObtenerMunProp";

import MPage from "../templates/mpage";
import FormInput from "../components/forminput";
import FormTextArea from "../components/formtextarea";
import EliminarPropuesta from "../components/eliminarpropuesta";
import Cargando from "../components/cargando";
import useTituloPagina from "../../utils/useTituloPagina";

const AdminPropuesta = (props) => {
  const { loggedIn } = useContext(AuthContext);
  const [usuario, errorUsuario] = useObtenerUsuario(loggedIn);
  const [municipio, propuesta, errorMunProp] = useObtenerMunProp(
    usuario,
    props.match.params.id,
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm();

  const [imagen, guardarImagen] = useState("");
  const [documento, guardarDocumento] = useState("");
  useTituloPagina(
    propuesta ? `Editar: ${propuesta.titulo}` : "Editar propuesta"
  );

  useEffect(() => {
    if (propuesta !== undefined) {
      const incializarForm = (propuesta) => {
        const camposPropuesta = Object.keys(propuesta);
        const camposAEliminar = [
          "doc",
          "img",
          "_id",
          "municipioId",
          "fecha",
          "observaciones",
          "__v",
          "observaciones",
        ];
        const camposFormulario = camposPropuesta.filter((campo) => {
          return !camposAEliminar.includes(campo);
        });
        for (const campo of camposFormulario) {
          setValue(campo, propuesta[campo], { shouldValidate: true });
        }
      };
      incializarForm(propuesta);
    }
  }, [propuesta, setValue]);

  //Guarda la imagen cargada en el formulario
  const actualizarImagen = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith("image/")) {
        Swal.fire(
          "Error",
          "Solo se permiten archivos de imagen (JPG o PNG)",
          "error",
        );
        e.target.value = "";
        return;
      }
      guardarImagen(file);
    }
  };

  //Guarda el documento cargado en el formulario
  const actualizarDocumento = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea un PDF
      if (file.type !== "application/pdf") {
        Swal.fire("Error", "Solo se permiten archivos PDF", "error");
        e.target.value = "";
        return;
      }

      // Validar tamaño (máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        Swal.fire(
          "Error",
          "El archivo es demasiado grande. Máximo 5MB permitido.",
          "error",
        );
        e.target.value = "";
        return;
      }

      guardarDocumento(file);
    }
  };

  const onFormSubmit = async (data) => {
    data["observaciones"] = propuesta.observaciones;
    if (data.obs !== "") {
      const observacion = {
        mensaje: data.obs,
        fecha: new Date().toLocaleDateString(),
      };
      data["observaciones"].unshift(observacion);
      delete data.obs;
    }

    // Subir imagen si existe
    data["img"] = propuesta.img;
    if (imagen && imagen.size > 0) {
      try {
        const nuevoNombre = await subirArchivo(imagen);
        if (nuevoNombre === null) {
          Swal.fire(
            "Error",
            "Ocurrió error al subir la imagen de la propuesta, intente de nuevo.",
            "error",
          );
          return;
        }
        data["img"] = nuevoNombre;
      } catch (error) {
        console.error("Error subiendo imagen:", error);
        Swal.fire(
          "Error",
          "Ocurrió error al subir la imagen de la propuesta, intente de nuevo.",
          "error",
        );
        return;
      }
    }

    // Subir documento si existe
    data["doc"] = propuesta.doc;
    if (documento && documento !== "") {
      try {
        const nuevoNombre = await subirArchivo(documento);
        if (nuevoNombre === null) {
          Swal.fire(
            "Error",
            "Ocurrió error al subir la documentación anexa, intente de nuevo.",
            "error",
          );
          return;
        }
        data["doc"] = nuevoNombre;
      } catch (error) {
        console.error("Error subiendo documento:", error);
        Swal.fire(
          "Error",
          "Ocurrió error al subir la documentación anexa, intente de nuevo.",
          "error",
        );
        return;
      }
    }

    // Guardar la propuesta
    try {
      await axios.put(`/api/propuestas/${propuesta._id}`, data);
      Swal.fire("¡Propuesta actualizada con éxito!", "", "success");
      props.history.push("/consola");
    } catch (err) {
      console.error("Error actualizando propuesta:", err);
      //Si el servidor explica por qué rechazó el cambio (por ejemplo, una
      //regla del ciclo del presupuesto), se muestra ese motivo.
      Swal.fire(
        err.response && err.response.data && err.response.data.errorMessage
          ? err.response.data.errorMessage
          : "Ocurrió un error al actualizar la propuesta, por favor inténtelo de nuevo o pónganse en contacto con el Programa Raíz.",
        "",
        "error",
      );
    }
  };

  if (loggedIn === false) return <Redirect to="/login" />;
  if (errorMunProp === true || errorUsuario === true)
    return <Redirect to="/" />;
  if (
    usuario === undefined ||
    municipio === undefined ||
    propuesta === undefined
  )
    return <Cargando />;
  if (usuario.adminPropuestas === false) return <Redirect to="/consola" />;

  return (
    <MPage municipio={municipio}>
      <div className="row">
        <div className="col-md-12">
          <h1 className="ppr-titulo centrado my-2">Administrar Propuesta</h1>
          <EliminarPropuesta propuesta={propuesta} />
          <p>Formulario de edición de propuesta.</p>
          <small className=" text-muted">
            Los campos indicados con <b>*</b> son obligatorios.
          </small>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <h2 className="ppr-titulo h4">Sobre el Autor</h2>

            {municipio.nombreUrl !== "ballesteros2022" &&
              municipio.nombreUrl !== "ballesteros2023" &&
              municipio.nombreUrl !== "noetinger2022" &&
              municipio.nombreUrl !== "corraldebustos2026" && (
                <FormInput
                  register={register}
                  error={errors.autor}
                  label={"* Autor:"}
                  id={"autor"}
                  isRequired={true}
                  placeholder="Nombre y apellido del autor o nombre de la organización, según corresponda."
                />
              )}

            {(municipio.nombreUrl === "ballesteros2022" ||
              municipio.nombreUrl === "ballesteros2023" ||
              municipio.nombreUrl === "noetinger2022" ||
              municipio.nombreUrl === "corraldebustos2026") && (
              <FormInput
                register={register}
                error={errors.autor}
                label={"* Nombre de la organización:"}
                id={"autor"}
                isRequired={true}
                placeholder="Nombre de la organización que presenta la propuesta."
              />
            )}

            {municipio.nombreUrl !== "ballesteros2022" &&
              municipio.nombreUrl !== "ballesteros2023" &&
              municipio.nombreUrl !== "noetinger2022" &&
              municipio.nombreUrl !== "corraldebustos2026" && (
                <FormInput
                  register={register}
                  error={errors.equipo}
                  label={"Equipo:"}
                  id={"equipo"}
                  isRequired={false}
                  placeholder="De tratarse de una organización, descripción del equipo."
                />
              )}

            {(municipio.nombreUrl === "ballesteros2022" ||
              municipio.nombreUrl === "ballesteros2023" ||
              municipio.nombreUrl === "noetinger2022" ||
              municipio.nombreUrl === "corraldebustos2026") && (
              <FormInput
                register={register}
                error={errors.equipo}
                label={"* Nombre del representante:"}
                id={"equipo"}
                isRequired={true}
                placeholder="Nombre y apellido del representante de la organización que presenta la propuesta."
              />
            )}

            <FormInput
              register={register}
              error={errors.dni}
              label={"DNI:"}
              id={"dni"}
              isRequired={false}
              placeholder="De corresponder, DNI de quién presenta la propuesta."
              type="number"
            />

            <FormInput
              register={register}
              error={errors.fechaNacimiento}
              label={"Fecha de Nacimiento:"}
              id={"fechaNacimiento"}
              isRequired={false}
              placeholder="De corresponder, fecha de nacimiento de quién presenta la propuesta."
              type="date"
            />
            <FormInput
              register={register}
              error={errors.email}
              label={"* Email:"}
              id={"email"}
              isRequired={true}
              placeholder="Email de quién presenta la propuesta."
              type="email"
            />

            <FormInput
              register={register}
              error={errors.tel}
              label={"* Teléfono:"}
              id={"tel"}
              isRequired={true}
              placeholder="Teléfono de quién presenta la propuesta, no olvide el código de área.."
              type="tel"
            />

            <FormInput
              register={register}
              error={errors.direccion}
              label={"Dirección:"}
              id={"direccion"}
              isRequired={false}
              placeholder="De corresponder, dirección de quién presenta la propuesta."
              type="direccion"
            />

            <h2 className="ppr-titulo h4">Sobre la Propuesta</h2>

            <FormInput
              register={register}
              error={errors.titulo}
              label={"* Título:"}
              id={"titulo"}
              isRequired={true}
              placeholder="Título de la propuesta."
            />

            <FormInput
              register={register}
              error={errors.subtitulo}
              label={"* Sub-Título:"}
              id={"subtitulo"}
              isRequired={true}
              placeholder="Sub-título de la propuesta."
            />

            {municipio.ejes.length > 0 && (
              <div className="from-group mb-3">
                <label htmlFor="eje" className="font-weight-bold">
                  * Eje de la propuesta:
                </label>
                <select
                  type="text"
                  className="form-control"
                  id="eje"
                  defaultValue="0"
                  {...register("eje", {
                    validate: (value) => value !== "0",
                    required: true,
                  })}
                >
                  <option key="placeholder" value="0" disabled>
                    Seleccione uno de los siguientes ejes para su propuesta.
                  </option>
                  {municipio.ejes.map((eje) => (
                    <option key={eje} value={eje}>
                      {eje}
                    </option>
                  ))}
                </select>
                {errors.eje && (
                  <small className="text-danger form-text">
                    Campo Obligatorio.
                  </small>
                )}
              </div>
            )}

            <FormInput
              register={register}
              error={errors.presupuesto}
              label={"* Presupuesto:"}
              id={"presupuesto"}
              isRequired={true}
              placeholder="Monto aproximado para ejecutar la propuesta."
              type="number"
            />

            <FormTextArea
              register={register}
              error={errors.objetivos}
              label={"* Objetivos:"}
              id={"objetivos"}
              isRequired={true}
              placeholder="Detallar, de forma clara y concreta, el propósito principal que se espera alcanzar con la propuesta. Debe responder con precisión a la pregunta: “¿Qué se quiere lograr con este proyecto?”"
              rows={10}
            />

            <FormTextArea
              register={register}
              error={errors.beneficiarios}
              label={"* Beneficiarios:"}
              id={"beneficiarios"}
              isRequired={true}
              placeholder="Describir a quién o quiénes se dirige la propuesta, identificando el grupo o sector que será impactado de forma directa."
              rows={10}
            />

            <FormTextArea
              register={register}
              error={errors.laccion}
              label={"* Lugar de acción:"}
              id={"laccion"}
              isRequired={true}
              placeholder="Indicar la ubicación o área geográfica en donde se desarrollará la propuesta."
              rows={1}
            />

            <FormTextArea
              register={register}
              error={errors.resultados}
              label={"* Resultados esperados:"}
              id={"resultados"}
              isRequired={true}
              placeholder="Enunciar los beneficios tangibles e intangibles que se obtendrán con la implementación del proyecto."
              rows={10}
            />

            <FormTextArea
              register={register}
              error={errors.pejecucion}
              label={"* Periodo de ejecución:"}
              id={"pejecucion"}
              isRequired={true}
              placeholder="Indicar la duración estimada de la propuesta en meses."
              rows={1}
            />

            <FormTextArea
              register={register}
              error={errors.descripcion}
              label={"Descripción:"}
              id={"descripcion"}
              isRequired={true}
              placeholder="Descripción lo más detallada posible sobre la propuesta.."
              rows={10}
            />

            {propuesta.doc && (
              <div className="form-group ">
                <a
                  className="adminp-a"
                  href={`/api/public/${propuesta.doc}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  Documentación Anexa Actual
                </a>
              </div>
            )}
            <FormInput
              register={register}
              error={errors.doc}
              label={"Documentación (PDF máximo 5MB):"}
              id={"doc"}
              isRequired={false}
              placeholder="Documento en PDF que se adjunta a la propuesta."
              type="file"
              accept=".pdf,application/pdf" // Remover Excel
              onChange={actualizarDocumento}
            />

            <img
              src={`/api/public/${propuesta.img}`}
              className="img-fluid mx-auto d-block height-100"
              alt={propuesta.titulo}
              loading="lazy"
            />

            <FormInput
              register={register}
              error={errors.doc}
              label={"Imagen (.jpg o .png)::"}
              id={"img"}
              isRequired={false}
              placeholder="Imagen que se adjunta a la propuesta."
              type="file"
              accept="image/png, image/jpeg"
              onChange={actualizarImagen}
            />

            <h2 className="ppr-titulo h4">
              Estado y Observaciones de la Propuesta
            </h2>

            <div className="from-group mb-3">
              <label htmlFor="estado" className="font-weight-bold">
                * Estado:
              </label>
              <select
                type="text"
                className="form-control"
                id="estado"
                {...register("estado", {
                  required: true,
                })}
              >
                <option key="pendiente" value="pendiente">
                  Dejar como pendiente
                </option>
                <option key="aceptada" value="aceptada">
                  Aceptar la propuesta para votación.
                </option>
                <option key="archivada" value="archivada">
                  Archivar la propuesta.
                </option>
                <option key="seleccionada" value="seleccionada">
                  Seleccionar la propuesta para ejecución.
                </option>
                <option key="borrada" value="borrada">
                  Eliminar Propuesta.
                </option>
              </select>
              {errors.eje && (
                <small className="text-danger form-text">
                  Campo Obligatorio.
                </small>
              )}
            </div>

            <FormTextArea
              register={register}
              error={errors.obs}
              label={"Agregar Observación:"}
              id={"obs"}
              isRequired={false}
              placeholder="Observación sobre los cambios realizados a la propuesta."
              rows={4}
            />
            <FormInput
              register={register}
              error={errors.ejecucion}
              label={"Actualizar estado de ejecución:"}
              id={"ejecucion"}
              isRequired={false}
              placeholder="Actualizar estado ejecución de la propuesta."
            />

            <div className="text-center">
              <button
                type="submit"
                className="btn ppr-btn-verde font-weight-bold m-2 mr-5"
              >
                Guardar Cambios
              </button>
              <Link
                to={"/consola"}
                className="btn ppr-btn-verde font-weight-bold m-2"
              >
                Volver
              </Link>
            </div>
          </form>
        </div>
      </div>
    </MPage>
  );
};

export default withRouter(AdminPropuesta);
